import './UploadVideoDialog.scss'

import axios from 'axios'
import Button from 'lib/components/Button/Button'
import Dialog from 'lib/components/Dialog/Dialog'
import Spinner from 'lib/components/Spinner/Spinner'
import AuthContext from 'lib/context/AuthContext'
import createYurl from 'lib/util/createYurl'
import { reportError } from 'lib/util/errorHandler'
import isUniqueConstraintError from 'lib/util/isUniqueConstraintError'
import removeFileExt from 'lib/util/removeFileExt'
import uploadToS3 from 'lib/util/uploadToS3'
import ActionDialog from 'pages/Home/admin/ActionDialog/ActionDialog'
import React, { ChangeEvent, Dispatch, FormEvent, SetStateAction, useContext, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

export interface UploadVideoDialogProps {
  onRequestClose: ()=> void
  recordedWebinarId?: string
  videoId?: string
  videoUploadId?: string
}

interface UploadVideoMetadata {
  recordedWebinarId?: string
  videoId?: string
}

interface CaptionsFiles {
  [lang: string]: File
}

interface UploadCaptionsFileButtonProps {
  lang: 'de' | 'en' | 'es' | 'fr'
  file?: File
  setCaptionsFiles: Dispatch<SetStateAction<CaptionsFiles>>
}

async function uploadCaptions(
  accessToken: string,
  captionsFiles: CaptionsFiles,
  videoUploadId: string,
  uploadVideoMetadata: UploadVideoMetadata
) {
  const { recordedWebinarId, videoId } = uploadVideoMetadata
  const metadata = { 'video-upload-id': videoUploadId }
  const keyPrefix = `${process.env.THREESIXTY_TRAINING_UPLOADS_S3_PREFIX}/${recordedWebinarId || videoId}`

  for (const lang of Object.keys(captionsFiles)) {
    const file = captionsFiles[lang]
    const captionMeta = { ...metadata, 'captions-lang': lang }
    const { data: captionYurl } = await createYurl(file.name, captionMeta, accessToken, keyPrefix, 'public-read')
    await uploadToS3(file, captionMeta, captionYurl.url, captionYurl.type)
  }
}

async function createVideoUpload(
  file: File,
  accessToken: string,
  uploadVideoMetadata: UploadVideoMetadata,
  captionsFiles: CaptionsFiles
) {
  const { recordedWebinarId, videoId } = uploadVideoMetadata
  const headers = { Authorization: `Bearer ${accessToken}` }
  const { data: videoUpload } = await axios.post(
    `${process.env.THREESIXTY_TRAINING_URI}/admin/video-uploads`,
    { ...uploadVideoMetadata },
    { headers }
  )
  const metadata = { 'video-upload-id': videoUpload.id }
  const keyPrefix = `${process.env.THREESIXTY_TRAINING_UPLOADS_S3_PREFIX}/${recordedWebinarId || videoId}`

  await uploadCaptions(accessToken, captionsFiles, videoUpload.id, uploadVideoMetadata)

  const { data: yurl } = await createYurl(file.name, metadata, accessToken, keyPrefix, 'public-read')
  await uploadToS3(file, metadata, yurl.url, yurl.type)
}

async function createVideoAndVideoUpload(file: File, accessToken: string, captionsFiles: CaptionsFiles) {
  const headers = { Authorization: `Bearer ${accessToken}` }
  const { data: video } = await axios.post(
    `${process.env.THREESIXTY_TRAINING_URI}/admin/videos`,
    { name: removeFileExt(file.name) },
    { headers }
  )
  try {
    await createVideoUpload(file, accessToken, { videoId: video.id }, captionsFiles)
  } catch (err) {
    await axios.delete(`${process.env.THREESIXTY_TRAINING_URI}/admin/videos/${video.id}`, { headers })
    throw err
  }
}

function UploadCaptionsFileButton(props: UploadCaptionsFileButtonProps) {
  const { file, lang, setCaptionsFiles } = props
  const fileInput = useRef(null)
  const [t] = useTranslation()

  function onClick() {
    if (fileInput && fileInput.current) {
      fileInput.current.click()
    }
  }

  function onCaptionsInputChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files[0]
    setCaptionsFiles((captionsFiles: CaptionsFiles) => ({ ...captionsFiles, [lang]: file }))
  }

  return (
    <div className="upload-video-dialog__video-file-section">
      <Button type="button" variant="link" onClick={onClick}>
        {file ? file.name : t(`admin.uploadVideoDialog.captions.${lang}`)}
      </Button>
      <input type="file" accept=".vtt" ref={fileInput} onChange={onCaptionsInputChange} style={{ display: 'none' }} />
    </div>
  )
}

export default function UploadVideoDialog(props: UploadVideoDialogProps) {
  const { onRequestClose, recordedWebinarId, videoId, videoUploadId } = props
  const [t] = useTranslation()
  const [videoFile, setVideoFile] = useState(null)
  const [captionsFiles, setCaptionsFiles] = useState<CaptionsFiles>({})
  const [apiState, setApiState] = useState({ success: false, error: null, saving: false })
  const fileInput = useRef(null)
  const { getAccessToken } = useContext(AuthContext)
  const { success, error, saving } = apiState

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()

    if (!videoFile && !videoUploadId) {
      return setApiState({ success: false, error: t('admin.genericError'), saving: false })
    }
    setApiState({ success: false, error: null, saving: true })

    try {
      const accessToken = await getAccessToken()
      if (!videoFile) {
        await uploadCaptions(accessToken, captionsFiles, videoUploadId, { recordedWebinarId, videoId })
      } else if (videoId || recordedWebinarId) {
        await createVideoUpload(videoFile, accessToken, { recordedWebinarId, videoId }, captionsFiles)
      } else {
        await createVideoAndVideoUpload(videoFile, accessToken, captionsFiles)
      }
      setApiState({ success: true, error: null, saving: false })
    } catch (err) {
      if (isUniqueConstraintError(err)) {
        return setApiState({
          success: false,
          saving: false,
          error: t('admin.uploadVideoDialog.uniqueError')
        })
      }
      setApiState({ success: false, error: t('admin.genericError'), saving: false })
      reportError(err, 'Error uploading video')
    }

    if (fileInput.current && fileInput.current.target) {
      fileInput.current.target.value = null
    }
  }

  function onInputChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files[0]
    setVideoFile(file)
  }

  function onSelectFileClick() {
    if (fileInput && fileInput.current) {
      fileInput.current.click()
    }
  }

  function renderBody() {
    if (error) {
      return (
        <Dialog.Body className="upload-video-dialog__error">
          <div>{error}</div>
        </Dialog.Body>
      )
    }

    if (success) {
      return (
        <Dialog.Body className="upload-video-dialog__success">
          <div>{t('admin.uploadVideoDialog.success')}</div>
        </Dialog.Body>
      )
    }

    if (saving) {
      return (
        <Dialog.Body className="upload-video-dialog__saving">
          <div>{t('admin.uploadVideoDialog.saving')}</div>
          <Spinner size="medium" />
        </Dialog.Body>
      )
    }

    const buttonEnabled = videoUploadId ? !!Object.keys(captionsFiles).length || !!videoFile : !!videoFile

    return (
      <form onSubmit={onSubmit}>
        <Dialog.Body>
          <div className="upload-video-dialog__video-file-section">
            <Button type="button" variant="link" onClick={onSelectFileClick}>
              {videoFile ? videoFile.name : t('admin.uploadVideoDialog.chooseVideoFile')}
            </Button>
          </div>
          <h3>{t('admin.uploadVideoDialog.captions.header')}</h3>
          <UploadCaptionsFileButton lang="en" file={captionsFiles.en} setCaptionsFiles={setCaptionsFiles} />
          <UploadCaptionsFileButton lang="es" file={captionsFiles.es} setCaptionsFiles={setCaptionsFiles} />
          <UploadCaptionsFileButton lang="fr" file={captionsFiles.fr} setCaptionsFiles={setCaptionsFiles} />
          <UploadCaptionsFileButton lang="de" file={captionsFiles.de} setCaptionsFiles={setCaptionsFiles} />
        </Dialog.Body>
        <ActionDialog.Footer>
          <div>
            <ActionDialog.CancelButton onClick={onRequestClose} />
            <Button size="small" disabled={!buttonEnabled} loading={apiState.saving}>
              {videoUploadId ? t('admin.uploadVideoDialog.updateButton') : t('admin.uploadVideoDialog.uploadButton')}
            </Button>
          </div>
        </ActionDialog.Footer>
      </form>
    )
  }

  return (
    <Dialog type={success ? 'success' : error ? 'warning' : 'default'} onRequestClose={!saving && onRequestClose}>
      <Dialog.Header onRequestClose={!saving && onRequestClose}>
        <Dialog.Title>
          {videoUploadId ? t('admin.uploadVideoDialog.updateTitle') : t('admin.uploadVideoDialog.title')}
        </Dialog.Title>
      </Dialog.Header>
      {renderBody()}
      <input type="file" ref={fileInput} onChange={onInputChange} style={{ display: 'none' }} />
    </Dialog>
  )
}
